// components/Footer.tsx
import { Link } from "react-router-dom";
import { Zap, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const footerLinks = {
  Product: [
    { label: "SMS", href: "/products/sms" },
    { label: "Email", href: "/products/email" },
    { label: "WhatsApp", href: "/products/whatsapp-messaging" },
    { label: "USSD", href: "/products/ussd" },
  ],
  Company: [
    { label: "About", href: "/about" },
    { label: "Pricing", href: "/pricing" },
    { label: "API Docs", href: "/api-docs" },
    { label: "Contact", href: "/contact" },
  ],
  Resources: [
    { label: "Blog", href: "/blog" },
    { label: "FAQ", href: "/#faq" },
    { label: "Reports & Tools", href: "/report-and-tools" },
    { label: "Events", href: "/events" },
  ],
  Legal: [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Service", href: "#" },
    { label: "Cookie Settings", href: "#cookies", isCookieTrigger: true },
  ],
};

const Footer = () => {
  return (
    <footer className="bg-gray-950 text-gray-300">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand + Newsletter */}
          <div className="lg:col-span-2 space-y-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold font-poltawski text-white">
                SmarthiveSMS
              </span>
            </Link>
            <p className="text-gray-400 leading-relaxed max-w-sm">
              Africa's leading omnichannel messaging platform. Reach your customers with 99% deliverability.
            </p>
            
            {/* Newsletter */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-white">Stay updated</p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-xl focus-visible:ring-primary-500"
                />
                <Button className="bg-primary-600 hover:bg-primary-700 text-white rounded-xl px-6">
                  Subscribe
                </Button>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="grid grid-cols-2 md:flex gap-12 md:gap-20 md:px-20">
            {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
                {category}
              </h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    {(link as any).isCookieTrigger ? (
                      <button
                        type="button"
                        onClick={() => window.dispatchEvent(new CustomEvent("open-cookie-settings"))}
                        className="text-gray-400 hover:text-white transition-colors text-sm inline-flex items-center gap-1 group cursor-pointer text-left"
                      >
                        {link.label}
                        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-gray-400 hover:text-white transition-colors text-sm inline-flex items-center gap-1 group"
                      >
                        {link.label}
                        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} SmarthiveSMS. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {["Twitter", "LinkedIn", "GitHub"].map((social) => (
              <a
                key={social}
                href="#"
                className="text-gray-500 hover:text-white transition-colors text-sm"
              >
                {social}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
